import React from "react";
import styled from "styled-components";

import Map from "./Components/Map";
import { IR as irValues, times, directions, dates } from "./constants";
import "./index.css";
import { makeIds } from "./Components/utils";
import { CustomInput, SectionContainer } from "./Components/Custom";

const CalcWrapper = styled.div`
  box-sizing: border-box;
  width: 100vw;
  max-width: 500px;
  margin: 10vh auto;
  padding: 1rem;
  background-color: cornsilk;
`;

// Constant defining the width of one facade cell in metres. Cannot be modified
// by the user.
const CELL_WIDTH = 7.2;
const INITIAL_DIRECTION = { 1: "SW", 2: "Horizontal", 3: "NE" };
const INITIAL_STREET_WIDTH = 1.8;

export const TimeContext = React.createContext();
export const DirectionContext = React.createContext();
export const DateContext = React.createContext();

// This function handles the modification of the properties of the cells and the
// street.
const cellPropsReducer = (state, action) => {
  switch (action.type) {
    case "ADD_MATERIAL":
      return {
        ...state,
        [action.payload.cellId]: {
          ...state[action.payload.cellId],
          materials: {
            ...state[action.payload.cellId].materials,
            [action.payload.material]: action.payload.fraction,
          },
          allMats: [
            ...state[action.payload.cellId].allMats,
            action.payload.material,
          ],
        },
      };
    case "DELETE_MATERIAL":
    case "SET_HEIGHT":
      return {
        ...state,
        [action.payload.cellId]: {
          ...state[action.payload.cellId],
          height: action.payload.height,
          area: action.payload.height * CELL_WIDTH,
        },
      };
    case "SET_DIRECTION":
      let o = { ...state };
      action.payload.idSetLeft.forEach((cellId) => {
        o = {
          ...o,
          [cellId]: {
            ...o[cellId],
            direction: action.payload.direction[1],
          },
        };
      });
      action.payload.idSetRight.forEach((cellId) => {
        o = {
          ...o,
          [cellId]: {
            ...o[cellId],
            direction: action.payload.direction[3],
          },
        };
      });
      return o;
    case "UPDATE_STREET_WIDTH":
      return {
        ...state,
        gorge: {
          ...state["gorge"],
          area: action.payload.width * CELL_WIDTH,
        },
      };
    case "RESET":
      return init(action.payload);
    default:
      return state;
  }
};

// Creates the skeleton object to store the properties of all cells and the
// street.
const getInitialCellProps = (cellIds, direction) => {
  let o = {};

  cellIds.forEach((cellId) => {
    o = {
      ...o,
      [cellId]: { allMats: [], materials: {}, height: 0, direction },
    };
  });

  return o;
};

const init = ({ idSetLeft, idSetRight, nOfCells }) => ({
  ...getInitialCellProps(idSetLeft, INITIAL_DIRECTION[1]),
  ...getInitialCellProps(idSetRight, INITIAL_DIRECTION[3]),
  gorge: {
    allMats: [],
    materials: {},
    height: 0,
    area: INITIAL_STREET_WIDTH * nOfCells * CELL_WIDTH,
    direction: "Horizontal",
  },
});

export default function App() {
  // Width of the street.
  const [streetWidth, setStreetWidth] = React.useState(1.8);

  // Number of cells in the facades on either side of the street. The number
  // multiplied by CELL_WIDTH will give the length of the street.
  const [nOfCells, setNOfCells] = React.useState(1);

  // Direction the facade on the left in the calculator UI is facing.
  const [direction, setDirection] = React.useState({
    1: "SW",
    2: "Horizontal",
    3: "NE",
  });

  // Set the time to derive the irradiance data. Defaults to mean irradiance for
  // the day.
  const [time, setTime] = React.useState("Mean");

  // Set month
  const [date, setDate] = React.useState("Jun21");

  // Generates array of IDs to assign to each cell.
  const idSetLeft = React.useMemo(() => makeIds(nOfCells), [nOfCells]);
  const idSetRight = React.useMemo(() => makeIds(nOfCells), [nOfCells]);

  const initialCellProps = React.useMemo(
    () => init({ idSetLeft, idSetRight, nOfCells }),
    [idSetLeft, idSetRight, nOfCells],
  );

  // cellProps object contains the properties of the cells/street,
  // dispatchCellProps is a function to modify the cellProps Object.
  // ------------------------------------------------------------
  // cellProps = {
  //   ...,
  //   [id]: {
  //     materials: {
  //       mat1: mat1.fraction,
  //       mat2: mat2.function,
  //       ...
  //     },
  //     allMats: [mat1, mat2, ...],
  //     height: <number>,
  //     area: <number>,
  //     direction: <string>,
  //   },
  //   ...
  // }
  const [cellProps, dispatchCellProps] = React.useReducer(
    cellPropsReducer,
    initialCellProps,
  );

  // Multipliers.
  const [vIRMultiplier, setIRMultiplier] = React.useState(1.0);

  // Sets direction for all surfaces, 1 - left, 2 - street (fixed), 3 - right,
  const setDir = (dir) => {
    const i = directions.indexOf(dir) || 0;
    const i2 = (i + 4) % 8;

    console.log(i, i2);

    setDirection({
      1: directions[i],
      2: "Horizontal",
      3: directions[i2],
    });

    dispatchCellProps({
      type: "SET_DIRECTION",
      payload: {
        idSetLeft,
        idSetRight,
        direction: {
          1: directions[i],
          2: "Horizontal",
          3: directions[i2],
        },
      },
    });
  };

  const updateStreetWidth = (width) => {
    setStreetWidth(width);

    dispatchCellProps({
      type: "UPDATE_STREET_WIDTH",
      payload: {
        width,
      },
    });
  };

  React.useEffect(() => {
    dispatchCellProps({
      type: "RESET",
      payload: {
        idSetLeft,
        idSetRight,
        nOfCells,
      },
    });
  }, [idSetLeft, idSetRight, nOfCells]);

  return (
    <CalcWrapper>
      {/* Input to set the number of cells. */}
      <SectionContainer>
        <label htmlFor="n-of-cells-input">Number of cells</label>
        <CustomInput
          id="n-of-cells-input"
          type="number"
          value={nOfCells}
          onChange={(e) => setNOfCells(parseInt(e.target.value))}
        />
      </SectionContainer>
      {/* Input to set the street width in metres. */}
      <SectionContainer>
        <label htmlFor="street-width-input">Street width (in metres)</label>
        <CustomInput
          id="street-width-input"
          type="number"
          step="0.1"
          value={streetWidth}
          onChange={(e) => updateStreetWidth(parseFloat(e.target.value))}
        />
      </SectionContainer>
      {/* Set direction */}
      <Direction dir={direction[1]} setDirection={setDir} />
      {/* Set time */}
      <Time setTime={setTime} time={time} />
      {/* Set date */}
      <Date setDate={setDate} date={date} />
      {/* Set IR multiplier */}
      <IRMultiplier value={vIRMultiplier} setValue={setIRMultiplier} />
      <DateContext.Provider value={date}>
        <TimeContext.Provider value={time}>
          <DirectionContext.Provider value={direction}>
            <Map
              cellProps={cellProps}
              dispatchCellProps={dispatchCellProps}
              multiplier={vIRMultiplier}
              nOfCells={nOfCells}
              idSetLeft={idSetLeft}
              idSetRight={idSetRight}
              streetWidth={streetWidth}
              cellWidth={CELL_WIDTH}
            />
          </DirectionContext.Provider>
        </TimeContext.Provider>
      </DateContext.Provider>
    </CalcWrapper>
  );
}

const IRMultiplier = ({ value, setValue }) => {
  return (
    <SectionContainer>
      <label htmlFor="ir-multi-select">Sky conditions: </label>
      <select
        name="ir-multi"
        id="ir-multi-select"
        type="select"
        value={value}
        onChange={(e) => setValue(e.target.value)}>
        {Object.getOwnPropertyNames(irValues).map((cond) => (
          <option key={cond} value={irValues[cond].value}>
            {irValues[cond].label}
          </option>
        ))}
      </select>
    </SectionContainer>
  );
};

const Direction = ({ dir, setDirection }) => {
  return (
    <SectionContainer>
      <label htmlFor="seldir">Orientation of facade (left):</label>
      <select
        name="direction"
        id="seldir"
        type="select"
        value={dir}
        onChange={(e) => setDirection(e.target.value)}>
        {directions.map((dir) => (
          <option key={dir} value={dir}>
            {dir}
          </option>
        ))}
      </select>
    </SectionContainer>
  );
};

const Time = ({ setTime, time }) => {
  return (
    <SectionContainer>
      <label htmlFor="seltime">Time of day: </label>
      <select
        name="time"
        id="seltime"
        type="select"
        value={time}
        onChange={(e) => setTime(e.target.value)}>
        {times.map((time) => (
          <option key={time} value={time}>
            {time}
          </option>
        ))}
      </select>
    </SectionContainer>
  );
};

const Date = ({ setDate, date }) => {
  return (
    <SectionContainer>
      <label htmlFor="seldate">Month:</label>
      <select
        name="date"
        id="seldate"
        type="select"
        value={date}
        onChange={(e) => setDate(e.target.value)}>
        {dates.map((date) => (
          <option key={date} value={date}>
            {date}
          </option>
        ))}
      </select>
    </SectionContainer>
  );
};
